import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ProductImage from 'components/global/ProductImage';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ORDERS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const ReturnDetails = (props, ref) => {
	const { id, nextStep, setOrderID } = props;
	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();
	const { closeModal } = useModalRouter();

	const [items, setItems] = useState(null);
	const { t } = useTranslation();
	const isAuthorized = useAccessControl();
	const defaultWarehouseId = useSelector((state) => state.user.generalSettings.defaultWarehouse);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape(
					{
						branchID: yup
							.string()
							.nullable()
							.when('warehouseID', {
								is: null,
								then: (schema) => schema.required(() => t('errorMsg.branchAndWarehouseValidation'))
							}),
						warehouseID: yup
							.string()
							.nullable()
							.when('branchID', {
								is: null,
								then: (schema) => schema.required(() => t('errorMsg.branchAndWarehouseValidation'))
							}),
						comments: yup.string().nullable(),
						items: yup.array().of(
							yup.object().shape({
								qtyTobeReturned: yup
									.number()
									.nullable()
									.min(0)
									.max(yup.ref('qtyOrdered'), () => t('errorMsg.qtyReturnedValidation'))
									.required(),
								itemReturnReason: yup.string().nullable(),
								qtyOrdered: yup.number().nullable()
							})
						)
					},
					['branchID', 'warehouseID']
				)
				.required(),
		[t]
	);

	const fillForm = useCallback(async () => {
		if (!id) {
			showError();
			closeModal();

			return;
		}

		return await axiosPrivate
			.post(ORDERS.GET_RETURNED_ORDER_DETAILS, { ID: id })
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setItems(data.items);
					setOrderID(data.orderID);

					return {
						orderID: id,
						warehouseID: data?.warehouseID ?? null,
						branchID: data?.branchID ?? null,
						comments: data?.comments ?? null,
						items: data?.items.map((item) => ({
							ID: item.ID,
							qtyOrdered: item.qtyOrdered ?? null,
							quantityReturnRequested: item.quantityReturnRequested ?? null,
							qtyTobeReturned:
								item.qtyTobeReturned ?? item.quantityReturnRequested ?? item.qtyOrdered ?? 0,
							itemReturnReason: item.itemReturnReason ?? null
						}))
					};
				} else {
					AppLogger.error(response);
					showError();
					closeModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		setError,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading }
	} = formMethods;

	const onSubmit = (data) => {
		const items = data.items;
		if (items.some((item) => item.qtyOrdered > 0) && !items.some((item) => item.qtyTobeReturned > 0)) {
			showError({
				message: 'errorMsg.createReturnShipmentQTYToBeReturnedValidation'
			});
			items.forEach((item, index) => {
				if (item.qtyOrdered > 0) {
					setError(`items[${index}].qtyTobeReturned`, {
						type: 'manual',
						message: t('errorMsg.createReturnShipmentQTYToBeReturnedValidation')
					});
				}
			});
			return;
		}

		axiosPrivate
			.post(ORDER_ACTIONS.UPDATE_RETURN_ORDER, data)
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					setItems(data.items);
					nextStep();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	useImperativeHandle(
		ref,
		() => ({
			async save() {
				await handleSubmit(onSubmit, (errors) => {
					AppLogger.log(errors);
				})();
			}
		}),
		[]
	);

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const autocompleteColumns = useMemo(
		() => [
			{ accessor: 'Name', width: 200 },
			{ accessor: 'Code', width: 150 }
		],
		[]
	);
	const autocompleteLabel = useMemo(() => ['Name'], []);

	const defaultColSpan = {
		md: 6
	};

	const columns = useMemo(
		() => [
			{
				header: 'productName',
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				size: 150
			},
			{
				header: 'sku',
				accessorKey: 'sku',
				size: 100
			},
			{
				header: 'orderedQuantity',
				accessorKey: 'qtyOrdered',
				size: 100
			},
			{
				header: 'qtyReturnRequested',
				accessorKey: 'quantityReturnRequested',
				size: 100
			},
			{
				header: 'qtyTobeReturned',
				renderCell: ({ row }) => <IntegerInput name={`items[${row.index}].qtyTobeReturned`} />,
				size: 100
			},
			{
				header: 'returnReason',
				renderCell: ({ row }) => <StringInput name={`items[${row.index}].itemReturnReason`} />,
				size: 400
			}
		],
		[items]
	);

	useEffect(() => {
		if (!isAuthorized('MultiWarehouseModule') && !isAuthorized('BranchModule')) {
			setValue('warehouseID', defaultWarehouseId);
		}
	}, [isAuthorized]);

	return (
		<>
			<Form formMethods={formMethods}>
				<FormItem
					label="dropOfWarehouse"
					colSpan={defaultColSpan}
					isLoading={isLoading}
					roleName="MultiWarehouseModule">
					<AutoComplete
						name="warehouseID"
						autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
						columns={autocompleteColumns}
						selectionLabelAccessor={autocompleteLabel}
						value={watch('warehouseID')}
						setValue={(value, isFirstRender) => {
							setValue('warehouseID', value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							if (!isFirstRender && value?.ID) {
								setValue('branchID', null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}
						}}
					/>
				</FormItem>

				<FormItem label="dropOfBranch" colSpan={defaultColSpan} isLoading={isLoading} roleName="BranchModule">
					<AutoComplete
						name="branchID"
						autoCompleteName={AUTOCOMPLETE.BRANCHES}
						columns={autocompleteColumns}
						selectionLabelAccessor={autocompleteLabel}
						value={watch('branchID')}
						setValue={(value, isFirstRender) => {
							setValue('branchID', value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							if (!isFirstRender && value?.ID) {
								setValue('warehouseID', null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}
						}}
					/>
				</FormItem>

				<FormItem label="returnComment" isLoading={isLoading}>
					<TextArea name="comments" />
				</FormItem>

				<FormItem>
					<Table columns={columns} data={items} loading={isLoading} />
				</FormItem>
			</Form>
		</>
	);
};

export default forwardRef(ReturnDetails);
